import React, { useCallback } from "react";
import { useParams } from "react-router-dom";

import { makeStyles } from "tss-react/mui";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";

import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";

import { setPrevState } from "../../../../../util/common";
import { regexName } from "../../../../../util/regularExpress";

const useStyles = makeStyles()({
  Panel: {
    padding: "10px 25px",
    backgroundColor: "#FFFFFF",
    marginTop: 0,
    borderRadius: 10,
    boxShadow: "none",
  },
  PanelRow: {
    padding: "10px 0",
  },
  deleteReffConfirm: {
    "& span": {
      display: "block",
    },
  },
  criticalMessage: {
    marginLeft: "5px",
  },
  subCriticalMessage: {
    marginTop: "8px",
    marginLeft: "5px",
  },
});
function InfoSection({
  setEntityInfoState,
  setDeleteState,
  displayName,
  displayNameError,
  nameError,
  currentName,
  dataVaultEnabled,
  description,
  entityName,
}) {
  const { entityID } = useParams();

  const { classes } = useStyles();

  const updateEntityInfoStateValue = useCallback(
    (key, value) => {
      setPrevState(setEntityInfoState, { [key]: value });
    },
    [setEntityInfoState]
  );

  const handleName = useCallback(
    (str, dataState) => {
      const isValid = regexName(str, "alphaWithUnderscore");
      if (isValid) setPrevState(setEntityInfoState, { [dataState]: str });
    },
    [setEntityInfoState]
  );

  const handleRenameCancel = useCallback(() => {
    setEntityInfoState((prev) => ({ ...prev, currentName: prev.name }));
    setDeleteState((prev) => ({ ...prev, openConfirm: false, handleCancel: undefined }));
  }, [setDeleteState, setEntityInfoState]);

  const handleEntityRename = useCallback(() => {
    setEntityInfoState((prev) => ({ ...prev, name: prev.currentName }));
    setDeleteState((prev) => ({ ...prev, openConfirm: false, handleCancel: undefined }));
  }, [setDeleteState, setEntityInfoState]);

  const handleNameOnBlur = useCallback(
    (e) => {
      if (!entityID) {
        setEntityInfoState((prev) => ({ ...prev, name: prev.currentName }));
      } else if (entityName !== e.target.value) {
        setPrevState(setDeleteState, {
          openConfirm: true,
          confirmTitle: "Rename the system name",
          confirmMessage: (
            <span className={classes.deleteReffConfirm}>
              <span>Are you sure you want to rename this entity system name?</span>
              <div style={{ marginTop: "16px", display: "flex" }}>
                <span>
                  <ReportProblemOutlinedIcon sx={{ fontSize: "2rem", color: "#D32F2F" }} />
                </span>
                <div>
                  <span className={classes.criticalMessage} style={{ color: "#D32F2F" }}>
                    This will cause changes to the live and historical data tables and records in the data warehouse. If
                    you have any custom-defined procedures or jobs or reports that refer to the old system names, they
                    would need to be updated manually following this step. Only perform this step after performing a
                    thorough impact analysis!
                  </span>
                  <span className={classes.subCriticalMessage}>
                    If you simply intend to change the display name of your entity, cancel this prompt and update the
                    Display Name field instead.
                  </span>
                  <span className={classes.subCriticalMessage}>
                    Click `Cancel` to close this prompt without making any changes or Rename to proceed with the
                    renaming. Note that renaming can take significant time and cannot be interrupted.
                  </span>
                </div>
              </div>
            </span>
          ),
          handleClick: () => handleEntityRename(),
          handleCancel: () => handleRenameCancel(),
          confirmBtnName: "Rename",
          confirmBtnColor: "error",
          popupMaxWidth: "md",
        });
      }
    },
    [
      classes.criticalMessage,
      classes.deleteReffConfirm,
      classes.subCriticalMessage,
      entityID,
      entityName,
      handleEntityRename,
      handleRenameCancel,
      setDeleteState,
      setEntityInfoState,
    ]
  );

  return (
    <Box className={classes.Panel}>
      <Box maxWidth="xl">
        <Grid container className={classes.PanelRow} spacing={3}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              required
              variant="outlined"
              inputProps={{ maxLength: 50 }}
              value={displayName}
              error={!!displayNameError}
              helperText={displayNameError}
              onChange={(e) => updateEntityInfoStateValue("displayName", e.target.value)}
              fullWidth
              label="Display Name"
              autoFocus={!entityID}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              required
              variant="outlined"
              error={!!nameError}
              inputProps={{ maxLength: 50 }}
              helperText={nameError}
              value={currentName}
              disabled={entityID && !dataVaultEnabled}
              onChange={(e) => handleName(e.target.value, "currentName")}
              onBlur={handleNameOnBlur}
              fullWidth
              label="System Name"
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 8 }}>
            <TextField
              variant="outlined"
              value={description}
              inputProps={{ maxLength: 120 }}
              onChange={(e) => updateEntityInfoStateValue("description", e.target.value)}
              fullWidth
              label="Description"
              multiline
              minRows={2}
              maxRows={2}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

export default React.memo(InfoSection);
